import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, MoreVertical, Mail, KeyRound, Users, BookOpen, UserRound, Calendar, Layers, Check, X, UserPlus } from "lucide-react";
import "./students-page.css";
import "./teachers-page.css";

// Teachers page of My School (principal): summary cards, the teacher table, the classes nobody teaches and the invitations waiting for an
// answer. Every figure comes from the existing teacher list, the unassigned-classes list and the invitations list; the actions (change
// e-mail, reset password, remove, edit classes, assign, cancel) are the existing ones and are passed in.

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const classLabel = (c) => `${c.class_name} ${c.section}`.trim();
const shortClass = (c) => `${String(c.class_name || "").replace(/\s*(standard|std|class|grade)\s*/i, " ").trim()} ${String(c.section || "").replace(/^section\s*/i, "")}`.trim() || classLabel(c);
const dateText = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
function ago(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "Today" : d === 1 ? "Yesterday" : `${d} days ago`;
}

function Menu({ t, onEditClasses, onRemove }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <span className="stl-menu" ref={ref}>
      <button type="button" className="stl-dots-btn" aria-label={`More actions for ${t.name}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}><MoreVertical size={18} /></button>
      {open && (
        <div className="stl-pop" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onEditClasses(t); }}>Edit classes</button>
          <button type="button" role="menuitem" className="danger" onClick={() => { setOpen(false); onRemove(t); }} data-testid={`remove-teacher-${t.user_id}`}>Remove teacher</button>
        </div>)}
    </span>
  );
}

export default function TeachersList({ list, unassigned, invites, canAdd, addTitle, onAdd, onAssign, onEmail, onReset, onRemove, editing, editText, setEditText, startEdit, cancelEdit, saveClasses, onDismissClass, onDropInvite }) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => list.filter((t) => !q.trim() || t.name.toLowerCase().includes(q.trim().toLowerCase()) || (t.email || "").toLowerCase().includes(q.trim().toLowerCase())), [list, q]);
  const assigned = useMemo(() => new Set(list.flatMap((t) => (t.classes || []).map(classLabel))).size, [list]);
  const students = list.reduce((n, t) => n + (t.student_count || 0), 0);
  const cards = [
    { icon: Users, tone: "mint", value: list.length, label: "Teachers" },
    { icon: BookOpen, tone: "teal", value: assigned, label: "Classes Assigned" },
    { icon: UserRound, tone: "gold", value: students, label: "Total Students" },
  ];

  return (
    <div className="stpage tchpage" data-testid="teachers-tab">
      <header className="stl-head">
        <div style={{ position: "relative", zIndex: 1 }}>
          <h1 className="stl-title">My School</h1>
          <p className="stl-sub">Manage your teachers, class assignments and invitations.</p>
        </div>
        <div className="stl-deco" aria-hidden="true">
          <img className="leaf l" src={`${process.env.PUBLIC_URL}/dashboard/leaves-left.png`} alt="" />
          <img className="mosque" src={`${process.env.PUBLIC_URL}/dashboard/mosque-header.png`} alt="" />
          <img className="leaf r" src={`${process.env.PUBLIC_URL}/dashboard/leaves-right.png`} alt="" />
        </div>
      </header>

      <section className="stl-cards" aria-label="Teacher summary" data-testid="teachers-summary">
        {cards.map((c) => (
          <div className="stl-card stl-stat" key={c.label}>
            <span className={`stl-ico ${c.tone}`}><c.icon size={28} strokeWidth={1.7} /></span>
            <div><b>{c.value}</b><span>{c.label}</span></div>
          </div>))}
      </section>

      <section id="tp-teachers" className="tp-block">
        <div className="tp-block-head">
          <h2 className="tp-h2"><Users size={26} strokeWidth={1.7} /> Teachers</h2>
          <label className="stl-search tp-search"><Search size={18} aria-hidden="true" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teachers by name or email..." aria-label="Search teachers" data-testid="teachers-search" /></label>
          <button type="button" className="stl-add" data-testid="add-teacher" disabled={!canAdd} title={addTitle} onClick={onAdd}><Plus size={18} /> Add Teacher</button>
        </div>
        <div className="stl-card stl-table-card">
          <div className="stl-table-wrap">
            <table className="stl-table tp-table" data-testid="teachers-table">
              <thead><tr><th>Teacher</th><th>Classes</th><th className="c">Students</th><th className="c">Active</th><th>Last Sign-in</th><th className="r">Actions</th></tr></thead>
              <tbody>
                {rows.map((t) => {
                  return (
                    <tr key={t.user_id}>
                      <td>
                        <span className="stl-name"><i className="stl-av">{initials(t.name)}</i>
                          <span className="tp-who"><b>{t.name}</b><small>{t.email}</small>{t.must_reset_password && <em className="tp-flag">Hasn&apos;t signed in yet</em>}</span></span>
                      </td>
                      <td>
                        {editing === t.user_id ? (
                          <span className="tp-edit">
                            <input value={editText} onChange={(e) => setEditText(e.target.value)} placeholder="3 A, 4 B" aria-label="Classes" autoFocus />
                            <button type="button" aria-label="Save classes" onClick={() => saveClasses(t)}><Check size={16} /></button>
                            <button type="button" aria-label="Cancel" onClick={cancelEdit}><X size={16} /></button>
                          </span>
                        ) : (
                          <span className="tp-chips">
                            {(t.classes || []).length ? t.classes.map((c) => <span key={classLabel(c)} className="tp-chip" title={classLabel(c)}>{shortClass(c)}</span>) : <button type="button" className="tp-assign" onClick={() => startEdit(t)}>Assign classes</button>}
                          </span>
                        )}
                      </td>
                      <td className="c">{t.student_count}</td>
                      <td className="c">{t.active_students}</td>
                      <td>{t.last_active_at ? <span className="tp-last"><Calendar size={16} aria-hidden="true" /><span><b>{dateText(t.last_active_at)}</b><small>{ago(t.last_active_at)}</small></span></span> : <span className="tp-never">Never</span>}</td>
                      <td className="r">
                        <span className="tp-actions">
                          <button type="button" className="tp-btn" onClick={() => onEmail(t)} title={t.self_registered ? "Replace with another account" : "Change sign-in e-mail"} aria-label={`Change e-mail for ${t.name}`}><Mail size={15} /> E-mail</button>
                          {!t.self_registered && <button type="button" className="tp-btn" onClick={() => onReset(t)} title="Reset password" aria-label={`Reset password for ${t.name}`}><KeyRound size={15} /> Reset</button>}
                          <Menu t={t} onEditClasses={startEdit} onRemove={onRemove} />
                        </span>
                      </td>
                    </tr>);
                })}
                {!rows.length && <tr><td colSpan={6} className="stl-none">{list.length ? "No teachers match." : "No teachers yet."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="tp-two">
        <section id="tp-unassigned" className="stl-card tp-card" data-testid="unassigned-classes">
          <h2 className="tp-h3"><Layers size={24} strokeWidth={1.7} /> Unassigned Classes <span>({unassigned.length})</span></h2>
          {unassigned.length === 0 ? <p className="tp-muted">Every class has a teacher.</p> : (<>
            <p className="tp-muted">These classes have no teacher right now. Assign a teacher to ensure students can continue learning.</p>
            <div className="tp-uc-list">
              {unassigned.map((c) => (
                <div key={`${c.class_name}|${c.section}`} className="tp-uc" data-testid={`unassigned-${c.class_name}|${c.section}`}>
                  <span className="stl-av sm"><Users size={16} /></span>
                  <span className="tp-uc-main"><b>{classLabel(c)}</b><small>{c.students} student{c.students === 1 ? "" : "s"}</small></span>
                  {!c.students && <button type="button" className="tp-x" aria-label="Dismiss this empty class" title="Dismiss" onClick={() => onDismissClass(c)}><X size={16} /></button>}
                  <button type="button" className="tp-btn solid" onClick={() => onAssign(c)} disabled={!canAdd} title={addTitle}><UserPlus size={15} /> Assign Teacher</button>
                </div>))}
            </div>
          </>)}
        </section>

        <section id="tp-invites" className="stl-card tp-card" data-testid="teacher-invites">
          <h2 className="tp-h3"><Mail size={24} strokeWidth={1.7} /> Invitations <span>({invites.length})</span></h2>
          {invites.length === 0 ? <p className="tp-muted">No invitations are waiting. Use Add Teacher to invite someone.</p> : (
            <div className="tp-inv-list">
              {invites.map((i) => (
                <div key={i.invite_id} className="tp-inv" data-testid={`invite-${i.invite_id}`}>
                  <i className="stl-av">{initials(i.invitee_name)}</i>
                  <span className="tp-who"><b>{i.invitee_name}</b><small>{i.invitee_email}</small></span>
                  <span className="tp-inv-meta"><em className={`tp-status ${i.status === "declined" ? "declined" : ""}`}>{i.status === "declined" ? "Declined" : "Waiting for approval"}</em><small>{dateText(i.created_at)}</small></span>
                  <button type="button" className="tp-btn" onClick={() => onDropInvite(i)} data-testid={`cancel-invite-${i.invite_id}`}>{i.status === "declined" ? "Dismiss" : "Cancel"}</button>
                </div>))}
            </div>)}
        </section>
      </div>
    </div>
  );
}
